/**
 * The shop's catalogue model, built from the middleware's public price list
 * (`GET {middleware}/api/v2.0/catalogue/public`). The middleware is the source of truth:
 * it syncs the catalogue to Paddle and publishes what exists there, so this module never
 * knows a price id by heart. Pure functions — unit-tested in catalogue.test.js.
 *
 * Shop vocabulary: an EDITION (CORE, PROFESSIONAL, INFINITY, EDUCATION) is sold per
 * PLUGIN (Rhino / Grasshopper, nTop, Fusion 360) and per TERM (monthly, annual), for a
 * number of USERS (Paddle quantity; each unit carries `seatsPerUnit` seats). Plugin credit
 * packs are a separate group with no plugin or term.
 */

/**
 * Editions in display order, with the copy the cart shows.
 *
 * `listed` is whether the edition gets a card in the cart's edition picker. PROFESSIONAL and
 * INFINITY are sold, but not offered here: the shop front is CORE, EDUCATION and the credit
 * packs. They stay in the catalogue on purpose, so `?sku=`, `?priceId=` and the HubSpot
 * `mode=payment` links sales has already sent keep resolving and can still be paid — see
 * pickerGroups, which also keeps a deep-linked unlisted edition visible so the cart it
 * renders is not a card-less orphan. Offering one again is a one-word change.
 */
export const EDITIONS = [
  { code: 'COR', name: 'CORE', title: 'CORE Edition', blurb: 'Generative design with Spherene for one user.', logo: '/spherene-core-logo.png', listed: true },
  { code: 'PRO', name: 'PROFESSIONAL', title: 'PROFESSIONAL Edition', blurb: 'The full professional toolset for one user.', logo: '/spherene-pro-logo.png', listed: false },
  { code: 'INF', name: 'INFINITY', title: 'INFINITY Edition', blurb: 'Everything, including the HEX module, for one user.', logo: '/spherene-hex-logo.png', listed: false },
  { code: 'EDU', name: 'EDUCATION', title: 'EDUCATION (Educator / Faculty)', blurb: 'For educators — two users per licence, academic e-mail required.', logo: '/spherene-education-logo.png', listed: true },
];

/** The plugin credit packs group (kind PluginCreditPack). */
export const CREDITS_GROUP = { code: 'CRD', name: 'Plugin credits', title: 'Plugin Credit Packs', blurb: 'Credits for the paid features of every Spherene plugin.', logo: '/spherene-credits-logo.png' };

/** Platforms (the SKU's third slot) in display order. */
export const PLATFORMS = [
  { code: 'RHN', name: 'Rhino / Grasshopper' },
  { code: 'TOP', name: 'nTop' },
  { code: 'FUS', name: 'Fusion 360' },
];

/** Paddle localizes by billing country; the shop's currency switch picks a representative country so the country-scoped prices apply. */
export const CURRENCY_COUNTRIES = { CHF: 'CH', EUR: 'DE', USD: 'US' };

export const SUPPORTED_CURRENCIES = Object.keys(CURRENCY_COUNTRIES);

export function countryForCurrency(currency) {
  return CURRENCY_COUNTRIES[String(currency || '').toUpperCase()] || CURRENCY_COUNTRIES.EUR;
}

export function editionByCode(code) {
  return EDITIONS.find((e) => e.code === String(code || '').toUpperCase()) || null;
}

export function platformByCode(code) {
  return PLATFORMS.find((p) => p.code === String(code || '').toUpperCase()) || null;
}

export function termLabel(months) {
  const m = Number(months) || 0;
  if (m === 1) return 'Monthly';
  if (m === 12) return 'Annual';
  if (m > 0 && m % 12 === 0) return `${m / 12}-Year`;
  if (m > 0) return `${m}-Month`;
  return 'One-time';
}

/** Per-cycle suffix for a localized amount: "/ month", "/ year", "" for one-time. */
export function cycleSuffix(product) {
  if (!product || !product.billingPeriod) return '';
  const { interval, frequency } = product.billingPeriod;
  if (frequency === 1) return `/ ${interval}`;
  return `/ ${frequency} ${interval}s`;
}

/**
 * Normalises one middleware catalogue row into the shop's product shape. Rows the shop does
 * not sell (NXT plans and packs, anything without a price id) are dropped by the caller.
 */
export function normaliseRow(row) {
  if (!row || typeof row !== 'object') return null;
  const sku = typeof row.sku === 'string' ? row.sku.trim().toUpperCase() : '';
  const priceId = typeof row.priceId === 'string' ? row.priceId.trim() : '';
  if (!sku || !priceId) return null;

  const kind = String(row.kind || '');
  const isPlan = kind === 'PluginPlan';
  const isCredits = kind === 'PluginCreditPack';
  if (!isPlan && !isCredits) return null;

  const termMonths = Number(row.termMonths) || 0;
  const interval = typeof row.billingInterval === 'string' && row.billingInterval ? row.billingInterval : null;
  const billingPeriod = interval ? { interval, frequency: Number(row.billingFrequency) || 1 } : null;
  const amounts = {};
  if (row.amounts && typeof row.amounts === 'object') {
    for (const [code, value] of Object.entries(row.amounts)) {
      const n = Number(value);
      if (Number.isFinite(n) && n > 0) amounts[String(code).toUpperCase()] = n;
    }
  }

  const edition = isPlan ? editionByCode(row.groupCode) : null;
  const platform = isPlan ? platformByCode(row.platform) : null;
  if (isPlan && (!edition || !platform)) return null; // an edition or platform the shop does not know

  return {
    sku,
    kind,
    priceId,
    productName: typeof row.productName === 'string' ? row.productName : '',
    groupCode: isPlan ? edition.code : CREDITS_GROUP.code,
    groupName: isPlan ? edition.name : CREDITS_GROUP.name,
    platform: platform ? platform.code : null,
    platformName: platform ? platform.name : null,
    termMonths,
    termLabel: isPlan ? termLabel(termMonths) : (typeof row.termLabel === 'string' ? row.termLabel : ''),
    billingPeriod,
    maxQuantity: Math.max(1, Number(row.maxQuantity) || 1),
    seatsPerUnit: Math.max(0, Number(row.seatsPerUnit) || 0),
    credits: Math.max(0, Number(row.credits) || 0),
    currency: typeof row.currency === 'string' && row.currency ? row.currency.toUpperCase() : 'EUR',
    amounts,
    // Display strings filled by paddleService.localizePrices; the catalogue amount is the fallback.
    price_display: null,
    price_numeric: null,
  };
}

/** Normalises the middleware list; drops what the shop does not sell; stable order (edition, platform, term / credits ascending). */
export function normaliseCatalogue(rows) {
  const list = Array.isArray(rows) ? rows.map(normaliseRow).filter(Boolean) : [];
  const editionOrder = (code) => {
    const i = EDITIONS.findIndex((e) => e.code === code);
    return i < 0 ? EDITIONS.length : i;
  };
  const platformOrder = (code) => {
    const i = PLATFORMS.findIndex((p) => p.code === code);
    return i < 0 ? PLATFORMS.length : i;
  };
  return list.sort((a, b) =>
    editionOrder(a.groupCode) - editionOrder(b.groupCode)
    || platformOrder(a.platform) - platformOrder(b.platform)
    || a.termMonths - b.termMonths
    || a.credits - b.credits
    || a.sku.localeCompare(b.sku));
}

/** The groups that have at least one product, in display order: the editions, then credits. */
export function availableGroups(products) {
  const codes = new Set(products.map((p) => p.groupCode));
  const groups = EDITIONS.filter((e) => codes.has(e.code));
  if (codes.has(CREDITS_GROUP.code)) groups.push(CREDITS_GROUP);
  return groups;
}

/**
 * The groups the cart's edition picker offers, in display order: the listed editions that
 * have products, then credits. An unlisted edition reached by a deep link is kept so the
 * selected product still has its card and logo; it is never offered unprompted.
 */
export function pickerGroups(products, selectedGroupCode) {
  const selected = String(selectedGroupCode || '');
  return availableGroups(products).filter((g) => g.listed !== false || g.code === selected);
}

export function productsOf(products, groupCode) {
  return products.filter((p) => p.groupCode === groupCode);
}

/** Platforms sold for an edition, in display order. */
export function platformsOf(products, groupCode) {
  const codes = new Set(productsOf(products, groupCode).map((p) => p.platform).filter(Boolean));
  return PLATFORMS.filter((p) => codes.has(p.code));
}

/** Terms sold for an edition on a platform, ascending months. */
export function termsOf(products, groupCode, platform) {
  const months = new Set(productsOf(products, groupCode).filter((p) => p.platform === platform).map((p) => p.termMonths));
  return [...months].sort((a, b) => a - b).map((m) => ({ months: m, label: termLabel(m) }));
}

export function findPlan(products, groupCode, platform, termMonths) {
  return products.find((p) => p.groupCode === groupCode && p.platform === platform && p.termMonths === Number(termMonths)) || null;
}

export function findBySku(products, sku) {
  const wanted = String(sku || '').trim().toUpperCase();
  return wanted ? products.find((p) => p.sku === wanted) || null : null;
}

export function findByPriceId(products, priceId) {
  const wanted = String(priceId || '').trim();
  return wanted ? products.find((p) => p.priceId === wanted) || null : null;
}

/** Clamps a requested quantity into [1, product.maxQuantity]. */
export function clampQuantity(product, requested) {
  const max = product ? product.maxQuantity : 1;
  const n = Math.floor(Number(requested));
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.min(n, max);
}

/**
 * Resolves what the page should show from the URL. Precedence: `sku` (the September deep
 * link), then a legacy `priceId` (older HubSpot / marketing links — still resolved when the
 * price is active), then `edition` + optional `platform` / `term` (`1M` / `12M` or months),
 * else the first product of the first group. `users` sets the quantity. Returns the product
 * (or null when nothing is sold), the quantity, and a note when a requested id was not found.
 */
export function resolveSelection(products, params) {
  const get = (k) => (params && typeof params.get === 'function' ? params.get(k) : params ? params[k] : null);
  const notes = [];
  let product = null;

  const sku = get('sku');
  if (sku) {
    product = findBySku(products, sku);
    if (!product) notes.push(`The offer ${String(sku).toUpperCase()} is not available; choose a plan below.`);
  }

  const priceId = get('priceId');
  if (!product && priceId) {
    product = findByPriceId(products, priceId);
    if (!product) notes.push('This link points to an offer that is no longer available; choose a plan below.');
  }

  if (!product) {
    const edition = editionByCode(get('edition'));
    const groupCode = edition ? edition.code : (availableGroups(products)[0] || {}).code;
    if (groupCode) {
      const pool = productsOf(products, groupCode);
      if (groupCode === CREDITS_GROUP.code) {
        product = pool[0] || null;
      } else {
        const wantedPlatform = platformByCode(get('platform'));
        const platform = wantedPlatform && pool.some((p) => p.platform === wantedPlatform.code)
          ? wantedPlatform.code
          : (platformsOf(products, groupCode)[0] || {}).code;
        const termParam = String(get('term') || '').toUpperCase().replace(/M$/, '');
        const wantedMonths = Number(termParam);
        const terms = termsOf(products, groupCode, platform);
        const months = terms.some((t) => t.months === wantedMonths) ? wantedMonths : (terms[terms.length - 1] || {}).months;
        product = findPlan(products, groupCode, platform, months) || pool[0] || null;
      }
    }
  }

  const quantity = clampQuantity(product, get('users') || get('quantity') || 1);
  return { product, quantity, notes };
}

/** The customer-stage label the HubSpot function expects for a product. */
export function productTypeFor(product) {
  if (!product) return 'Student';
  switch (product.groupCode) {
    case 'COR': return 'Core';
    case 'EDU': return 'Faculty/ Educator';
    case 'PRO':
    case 'INF': return 'Enterprise';
    default: return 'Student';
  }
}

/** Formats a catalogue amount as the fallback display, e.g. "CHF 1'920.00". */
export function formatAmount(amount, currency) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '';
  return `${currency} ${n.toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Human label of a product for summaries and e-mails, e.g. "CORE Edition · Rhino / Grasshopper · Annual" or "500 plugin credits". */
export function productLabel(product) {
  if (!product) return '';
  if (product.groupCode === CREDITS_GROUP.code) {
    return product.credits > 0 ? `${product.credits.toLocaleString('de-CH')} plugin credits` : (product.termLabel || product.sku);
  }
  return [`${product.groupName} Edition`, product.platformName, product.termLabel].filter(Boolean).join(' · ');
}
